import React from 'react';

// react-pdf
import { Document, Page, pdfjs } from 'react-pdf';

// material icons / components
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import IconButton from '@material-ui/core/IconButton';
import PropTypes from 'prop-types';

import './RenderPDFPreview.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// On very wide desktops we cap the fit-to-width page so the text isn't blown up
// to an unreadable size; the user can still zoom past this with the + control.
const MAX_FIT_WIDTH = 900;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

/**
 * Responsive PDF viewer. The page is rendered to FIT THE CONTAINER WIDTH
 * (react-pdf `width` prop, measured live with a ResizeObserver) rather than a
 * fixed scale, so it looks right on phones and desktops alike. Zoom multiplies
 * that fit-width and the page scrolls inside the viewport when it overflows.
 */
class RenderPDFPreview extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      numPages: null,
      pageNumber: 1,
      attemptFailed: false,
      containerWidth: 0,
      zoom: 1,
    };
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.measure();
    if (typeof ResizeObserver !== 'undefined' && this.containerRef.current) {
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.containerRef.current);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.measure);
    }
  }

  componentWillUnmount() {
    if (this.ro) this.ro.disconnect();
    else if (typeof window !== 'undefined') window.removeEventListener('resize', this.measure);
  }

  measure = () => {
    const el = this.containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w && w !== this.state.containerWidth) this.setState({ containerWidth: w });
  };

  // react-pdf reloads the document whenever the `file` prop CHANGES IDENTITY, and
  // a reload resets us to page 1. Building `{ data: pdfData }` inline would create
  // a new object on every render (page change, zoom, resize), so paging never
  // stuck. Cache the wrapper and only rebuild it when the underlying bytes change.
  getFileProp = () => {
    const pdfData = this.props.data?.pdfData;
    if (this._pdfDataRef !== pdfData) {
      this._pdfDataRef = pdfData;
      this._fileProp = pdfData ? { data: pdfData } : null;
    }
    return this._fileProp;
  };

  onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ numPages, attemptFailed: false, pageNumber: 1 });
    if (this.props.handlePdfLoadSuccess) this.props.handlePdfLoadSuccess();
  };

  onDocumentLoadError = () => {
    this.setState({ attemptFailed: true });
    if (this.props.handlePdfLoadError) this.props.handlePdfLoadError();
  };

  goTo = (n) => this.setState({ pageNumber: n });
  zoomIn = () => this.setState((s) => ({ zoom: Math.min(ZOOM_MAX, +(s.zoom + ZOOM_STEP).toFixed(2)) }));
  zoomOut = () => this.setState((s) => ({ zoom: Math.max(ZOOM_MIN, +(s.zoom - ZOOM_STEP).toFixed(2)) }));
  resetZoom = () => this.setState({ zoom: 1 });

  renderNav(compact) {
    const { numPages, pageNumber } = this.state;
    if (!numPages) return null;
    return (
      <div className="pdfv-nav">
        <IconButton
          size="small"
          disabled={pageNumber <= 1}
          onClick={() => this.goTo(pageNumber - 1)}
          aria-label="Previous page"
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
        <span className="pdfv-count">
          {compact ? `${pageNumber} / ${numPages}` : `Page ${pageNumber} of ${numPages}`}
        </span>
        <IconButton
          size="small"
          disabled={pageNumber >= numPages}
          onClick={() => this.goTo(pageNumber + 1)}
          aria-label="Next page"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </div>
    );
  }

  render() {
    const { numPages, pageNumber, attemptFailed, containerWidth, zoom } = this.state;

    // Fit-to-width base (capped on large screens); zoom scales it up/down.
    const gutter = 24; // matches .pdfv-scroll horizontal padding
    const fitWidth = containerWidth ? Math.min(containerWidth - gutter, MAX_FIT_WIDTH) : 0;
    const pageWidth = fitWidth > 0 ? fitWidth * zoom : undefined;

    return (
      <div className="pdfv-root" ref={this.containerRef}>
        {numPages && !attemptFailed && (
          <div className="pdfv-toolbar">
            {this.renderNav(true)}
            <div className="pdfv-zoom">
              <IconButton size="small" disabled={zoom <= ZOOM_MIN} onClick={this.zoomOut} aria-label="Zoom out">
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <button type="button" className="pdfv-zoomlvl" onClick={this.resetZoom} title="Reset zoom">
                {Math.round(zoom * 100)}%
              </button>
              <IconButton size="small" disabled={zoom >= ZOOM_MAX} onClick={this.zoomIn} aria-label="Zoom in">
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        )}

        <div className="pdfv-scroll">
          {attemptFailed ? (
            <p className="pdfv-error">Failed to load the PDF. Please try again later.</p>
          ) : (
            <Document
              file={this.getFileProp()}
              onLoadSuccess={this.onDocumentLoadSuccess}
              onLoadError={this.onDocumentLoadError}
              loading={
                <div className="pdfv-loading">
                  <div className="pdfv-spinner" />
                  <span>Loading preview…</span>
                </div>
              }
              error={<p className="pdfv-error">Failed to load the PDF. Please try again later.</p>}
              className="pdfv-doc"
            >
              {pageWidth && (
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={<div className="pdfv-loading"><div className="pdfv-spinner" /></div>}
                  className="pdfv-page"
                />
              )}
            </Document>
          )}
        </div>
      </div>
    );
  }
}

RenderPDFPreview.propTypes = {
  data: PropTypes.object,
  handlePdfLoadSuccess: PropTypes.func,
  handlePdfLoadError: PropTypes.func,
};

RenderPDFPreview.defaultProps = {
  data: {},
  handlePdfLoadSuccess: () => {},
  handlePdfLoadError: () => {},
};

export default RenderPDFPreview;
